import { useMemo, type ReactNode } from 'react'
import { ArrowUpRight, Info } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { MetricLabel } from '@/components/finance/MetricLabel'
import { useCurrency } from '@/contexts/CurrencyContext'
import { formatMoneyFixed } from '@/lib/currency'
import type { MetricKey } from '@/lib/financeGlossary'
import { cn } from '@/lib/utils'

/**
 * The expense dashboard's shared vocabulary (F-FE-N164).
 *
 * One colour rule for the whole page: figures are ink. Red means a loss and
 * nothing else — a cost is not an error, so a column of expenses is not red.
 * Blue and the two warm steps belong to chart marks, where they are keyed by a
 * legend; text never wears them.
 */

/** Series identities, as CSS variables defined per theme in index.css. */
export const MONEY_SERIES = {
  sales: 'var(--chart-sales)',
  cogs: 'var(--chart-cogs)',
  opex: 'var(--chart-opex)',
  loss: 'var(--chart-loss)',
  neutral: 'hsl(var(--muted-foreground) / 0.45)',
} as const

export type MoneySeries = keyof typeof MONEY_SERIES

export function useMoney() {
  const { currencyCode } = useCurrency()
  return useMemo(() => {
    const symbol =
      new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol' })
        .formatToParts(0)
        .find(p => p.type === 'currency')?.value ?? currencyCode
    const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
    return {
      /** "Rs" — for a caption that says what a column of bare figures is in. */
      symbol: currencyCode === 'PKR' ? 'Rs' : symbol,
      /** "Rs 410,370.80" — a figure standing on its own. */
      full: (n: number) => formatMoneyFixed(n, currencyCode),
      /** "Rs 410,371" — a headline, where the paisa is noise. */
      whole: (n: number) => formatMoneyFixed(n, currencyCode, { digits: 0 }),
      /** "410,370.80" — one cell in a column that is captioned with the currency. */
      plain: (n: number) => formatMoneyFixed(n, currencyCode, { symbol: false }),
      /** "410K" — an axis tick. */
      axis: (n: number) => (n < 0 ? `−${compact.format(Math.abs(n))}` : compact.format(n)),
    }
  }, [currencyCode])
}

/** A chart key: the mark's shape in the series colour, the name in ink. */
export function SeriesKey({ series, children, className }: { series: MoneySeries; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs text-muted-foreground', className)}>
      <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: MONEY_SERIES[series] }} />
      {children}
    </span>
  )
}

/**
 * A category, the same way on every tab: the tenant's colour as a small dot,
 * the name in ink. The definitions store a badge class pair
 * ("bg-purple-100 text-purple-800 dark:bg-purple-950 …"); `bg-current` paints
 * the dot in the pair's text colour, which is the saturated one, and the
 * `dark:` twin stops the pair's own dark background winning in dark mode.
 */
export function CategoryLabel({ label, color, className }: { label: string; color?: string; className?: string }) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', color, 'bg-current dark:bg-current')} />
      <span className="truncate text-foreground">{label}</span>
    </span>
  )
}

/** A label that explains itself: glossary entry when there is one, else the copy given. */
export function TermLabel({
  label,
  metric,
  help,
  className,
  hideIcon = true,
}: {
  label: string
  metric?: MetricKey
  help?: string
  className?: string
  hideIcon?: boolean
}) {
  if (metric && !help) return <MetricLabel metric={metric} label={label} className={className} hideIcon={hideIcon} />
  if (!help) return <span className={className}>{label}</span>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className={cn(
            'inline-flex cursor-help items-center gap-1 rounded-sm underline-offset-2 decoration-dotted hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <span>{label}</span>
          {!hideIcon && <Info className="h-3 w-3 text-muted-foreground/70" aria-hidden />}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {help}
      </TooltipContent>
    </Tooltip>
  )
}

export interface KpiCell {
  label: string
  metric?: MetricKey
  help?: string
  value: string
  /** The exact figure, shown on hover when the headline is rounded. */
  exact?: string
  /** The line under the figure: a ratio, a count, what it is measured against. */
  sub?: ReactNode
  /** A loss. The only tone a figure takes. */
  negative?: boolean
  /** Where a click goes, in the reader's words ("Open the ledger"). */
  hint?: string
  onClick?: () => void
}

/**
 * The period's headline figures as one ruled strip rather than a row of
 * separate cards — they are one statement read left to right, and four boxes
 * with four icons said otherwise.
 */
export function KpiStrip({ cells, className }: { cells: KpiCell[]; className?: string }) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border shadow-sm',
        cells.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
        className,
      )}
    >
      {cells.map(c => {
        const body = (
          <>
            <div className="flex items-center justify-between gap-2">
              <TermLabel label={c.label} metric={c.metric} help={c.help} className="text-sm text-muted-foreground" />
              {c.onClick && (
                <ArrowUpRight
                  className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                  aria-hidden
                />
              )}
            </div>
            <p
              className={cn('mt-2 text-2xl font-semibold tracking-tight', c.negative ? 'text-danger-fg' : 'text-foreground')}
              title={c.exact}
            >
              {c.value}
            </p>
            {c.sub && <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{c.sub}</div>}
          </>
        )
        if (!c.onClick) {
          return (
            <div key={c.label} className="bg-card p-5">
              {body}
            </div>
          )
        }
        return (
          <div
            key={c.label}
            role="button"
            tabIndex={0}
            aria-label={c.hint ? `${c.label}: ${c.value}. ${c.hint}` : undefined}
            onClick={c.onClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                c.onClick?.()
              }
            }}
            className="group cursor-pointer bg-card p-5 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            {body}
          </div>
        )
      })}
    </div>
  )
}
